import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, Play, Pause, Volume2, VolumeX, Maximize2, Award, Target, Globe, ExternalLink } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '@/contexts/LanguageContext';
import { db } from '@/lib/supabase';

interface ActivityEvent {
  id: string;
  title: string;
  description: string;
  date: string;
  location: string;
  attendees: string;
  duration: string;
  category: string;
  color_scheme: string;
  image_url: string;
  video_url: string | null;
  youtube_url: string | null;
  media_type: 'image' | 'video' | 'youtube';
  carousel_order: number;
  is_active: boolean;
}

const ActivitiesCarousel: React.FC = () => {
  const [events, setEvents] = useState<ActivityEvent[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [videoCompleted, setVideoCompleted] = useState(false);
  const [youtubeCompleted, setYoutubeCompleted] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const youtubeRef = useRef<HTMLIFrameElement>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const { t } = useLanguage();

  useEffect(() => {
    loadEvents();
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (isPlaying && events.length > 0) {
      const currentEventData = events[currentIndex];
      if (!currentEventData) return;
      
      const shouldWaitForVideo = 
        (currentEventData.media_type === 'video' && !videoCompleted);
      
      if (shouldWaitForVideo) {
        return; // Don't start interval if video not completed
      }
      
      intervalRef.current = setInterval(() => {
        nextSlide();
      }, 5000);
    } else if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isPlaying, currentIndex, events.length, videoCompleted, youtubeCompleted]);

  useEffect(() => {
    // Reset video completion flags when changing slides
    setVideoCompleted(false);
    setYoutubeCompleted(false);
  }, [currentIndex]);

  const loadEvents = async () => {
    try {
      setIsLoading(true);
      const data = await db.activities.getActive();
      const sortedEvents = data.sort((a, b) => a.carousel_order - b.carousel_order);
      setEvents(sortedEvents);
      setIsLoading(false);
    } catch (error) {
      console.error('Error loading events:', error);
      setEvents([]);
      setIsLoading(false);
    }
  };

  const nextSlide = (manualOverride = false) => {
    if (manualOverride) {
      setCurrentIndex((prev) => (prev + 1) % events.length);
    } else {
      if (events.length === 0) return;
      const currentEventData = events[currentIndex];
      if (!currentEventData) return;
      
      const shouldAutoAdvance = 
        currentEventData.media_type === 'image' ||
        (currentEventData.media_type === 'video' && videoCompleted);
      
      if (shouldAutoAdvance) {
        setCurrentIndex((prev) => (prev + 1) % events.length);
      }
    }
  };

  const prevSlide = (manualOverride = false) => {
    setCurrentIndex((prev) => (prev - 1 + events.length) % events.length);
  };

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
  };

  const togglePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
    }
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const getGradientClass = (colorScheme: string) => {
    return colorScheme || 'from-blue-500 to-purple-600';
  };

  const getYoutubeVideoId = (url: string) => {
    if (!url) return null;
    
    // Common YouTube URL patterns
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
      /youtube\.com\/watch\?.*v=([^&\n?#]+)/,
      /youtube\.com\/v\/([^&\n?#]+)/,
      /youtube\.com\/shorts\/([^&\n?#]+)/
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1] && match[1].length >= 8) {
        return match[1];
      }
    }
    
    return null;
  };

  const getYoutubeEmbedUrl = (url: string) => {
    const videoId = getYoutubeVideoId(url);
    return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
  };

  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const [videoDuration, setVideoDuration] = useState<number | null>(null);

  const handleLoadedMetadata = (e: React.SyntheticEvent<HTMLVideoElement>) => {
    const video = e.currentTarget;
    setVideoDuration(video.duration);
  };

  const handleVideoEnded = () => {
    setVideoCompleted(true);
  };

  const handleVideoPlay = () => {
    setVideoCompleted(false);
  };


  if (isLoading) {
    return (
      <section className="py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center h-96">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        </div>
      </section>
    );
  }

  if (events.length === 0) {
    return (
      <section className="py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Naše aktivity</h2>
          <p className="text-gray-400">Žiadne aktivity na zobrazenie</p>
        </div>
      </section>
    );
  }

  const currentEvent = events[currentIndex];

  return (
    <section id="activities" className="py-16 bg-gradient-to-br from-slate-200 via-slate-100 to-slate-200 relative overflow-hidden">
      {/* Background Pattern - odstránený prehľad */}
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-8"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-slate-800 mb-6 leading-tight">
            Naše aktivity
          </h2>
          <p className="text-lg md:text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
            Naše najvýznamnejšie úspechy a aktivity.
          </p>
        </motion.div>

        {/* Carousel Container */}
        <div className="max-w-7xl mx-auto">
          <div className="relative bg-white rounded-2xl border border-slate-200 shadow-xl overflow-hidden grid grid-cols-1 lg:grid-cols-12">
            {/* Media Display - Larger on larger screens */}
            <div className="lg:col-span-8 relative aspect-video bg-white">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentEvent.id}
                  initial={{ opacity: 0, scale: 1.05 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.3 }}
                  className="absolute inset-0 flex items-center justify-center bg-transparent"
                >
                  {currentEvent.media_type === 'image' && (
                    <img
                      src={currentEvent.image_url || '/placeholder-activity.jpg'}
                      alt={currentEvent.title}
                      className="w-full h-full object-contain max-w-[90%] max-h-[90%]"
                    />
                  )}
                  {currentEvent.media_type === 'youtube' && (
                    <iframe
                      ref={youtubeRef}
                      src={`${getYoutubeEmbedUrl(currentEvent.youtube_url || '') || ''}?autoplay=1&mute=1&controls=1&playsinline=1&rel=0&showinfo=0`}
                      className="w-full h-full"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                      title={currentEvent.title}
                    />
                  )}
                  {currentEvent.media_type === 'video' && (
                    <video
                      ref={videoRef}
                      src={currentEvent.video_url || ''}
                      className="w-full h-full object-cover"
                      controls
                      controlsList="nodownload noremoteplayback"
                      preload="metadata"
                      poster={currentEvent.image_url || ''}
                      onLoadedMetadata={handleLoadedMetadata}
                      onEnded={handleVideoEnded}
                      onPlay={handleVideoPlay}
                      autoPlay
                      muted
                      playsInline
                    />
                  )}
                </motion.div>
              </AnimatePresence>

              {/* Gradient Overlay - úplne odstránený */}

              {/* Navigation Arrows */}
              <button
                onClick={() => prevSlide(true)}
                className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-white/70 hover:bg-white/90 text-gray-800 rounded-full transition-all duration-300 hover:scale-110 z-10"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                onClick={() => nextSlide(true)}
                className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-white/70 hover:bg-white/90 text-gray-800 rounded-full transition-all duration-300 hover:scale-110 z-10"
              >
                <ChevronRight className="w-6 h-6" />
              </button>

              {/* Media Controls */}
              <div className="absolute bottom-4 right-4 flex items-center gap-2">
                {currentEvent.media_type === 'video' && videoDuration && (
                  <span className="text-gray-800 text-sm bg-white/70 px-2 py-1 rounded">
                    {formatDuration(videoDuration)}
                  </span>
                )}
                {currentEvent.media_type === 'video' && (
                  <button
                    onClick={togglePlayPause}
                    className="p-2 bg-white/70 rounded-full hover:bg-white/90 text-gray-800 transition-all duration-300"
                  >
                    {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                  </button>
                )}
              </div>
            </div>

            {/* Content Section */}
            <div className="lg:col-span-4 p-6 md:p-8 lg:p-10 flex flex-col justify-center">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentEvent.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-6"
                >
                  {/* Header */}
                  <div>
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-gradient-to-r ${getGradientClass(currentEvent.color_scheme)} text-white mb-2`}>
                          {currentEvent.category}
                        </span>
                        <h3 className="text-xl md:text-2xl font-bold text-slate-800 leading-tight">
                          {currentEvent.title}
                        </h3>
                      </div>
                    </div>
                    <p className="text-gray-600 text-sm md:text-base leading-relaxed">
                      {currentEvent.description}
                    </p>
                  </div>

                  {/* Event Details - Removed date, location, attendees, and duration */}

                </motion.div>
              </AnimatePresence>
            </div>

            {/* Dots Navigation */}
            <div className="flex justify-center pb-8 gap-2">
              {events.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    index === currentIndex
                      ? 'bg-blue-500 scale-125'
                      : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};

export default ActivitiesCarousel;